import styled from '@emotion/styled';
import Link from '@ep/components/Link';
import icons from '@ep/images/icons';
import theme from '@ep/theme';
import React from 'react';

export type ButtonSize = 'medium' | 'large';

export type ButtonColor =
  | 'blue'
  | 'purple'
  | 'green'
  | 'gray'
  | 'black'
  | 'white';

export interface ButtonProps {
  label?: string;
  link?: string;
  icon?: string;
  type?: 'button' | 'reset' | 'submit';
  rightIcon?: string;
  size: ButtonSize;
  color: ButtonColor;
  outlined: boolean;
  disabled: boolean;
  onClick?: () => void;
  children?: unknown;
  state?: unknown;
  reusedAt?: string;
  id?: string | number;
}

interface StyledButtonProps {
  hasLabel: boolean;
  size: ButtonSize;
  color: ButtonColor;
  outlined: boolean;
  reusedAt?: string;
}

const colorMap = {
  white: {
    color: theme.colors.darkGray,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.darkGray,
    hoverBackgroundColor: theme.colors.mantisGreen,
    hoverBorderColor: theme.colors.mantisGreen,
  },
  blue: {
    color: theme.colors.white,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.mediumBlue,
    borderColor: theme.colors.mediumBlue,
    hoverBackgroundColor: theme.colors.lightBlue,
    hoverBorderColor: theme.colors.lightBlue,
  },
  purple: {
    color: theme.colors.white,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.darkPurple,
    borderColor: theme.colors.darkPurple,
    hoverBackgroundColor: theme.colors.mediumPurple,
    hoverBorderColor: theme.colors.mediumPurple,
  },
  green: {
    color: theme.colors.white,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.mantisGreen,
    borderColor: theme.colors.mantisGreen,
    hoverBackgroundColor: theme.colors.mediumGreen,
    hoverBorderColor: theme.colors.mediumGreen,
  },
  gray: {
    color: theme.colors.darkBlue,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.lightGray,
    borderColor: theme.colors.lightGray,
    hoverBackgroundColor: theme.colors.gray,
    hoverBorderColor: theme.colors.gray,
  },
  black: {
    color: theme.colors.white,
    hoverColor: theme.colors.white,
    backgroundColor: theme.colors.darkGray,
    borderColor: theme.colors.darkGray,
    hoverBackgroundColor: theme.colors.mantisGreen,
    hoverBorderColor: theme.colors.mantisGreen,
  },
} as const;

const StyledButton = styled.button<StyledButtonProps>(
  ({ size, color, outlined, reusedAt }) => `
    white-space: wrap;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    border: 1px solid ${colorMap[color].borderColor};
    border-radius: ${theme.radius.large};
    min-width: fit-content;
    background-color: ${
      outlined ? theme.colors.white : colorMap[color].backgroundColor
    };
    padding: 0 ${size === 'large' ? 1.6 : 1.5}rem;
    height: ${size === 'large' ? 5 : 3.75}rem;
    color: ${
      outlined ? colorMap[color].backgroundColor : colorMap[color].color
    };
    font-family: ${theme.typography.primaryFont.regular};
    font-size: 1.5rem;
    ${theme.mediaQueries.mobile} {
        font-size: 1.2rem;
        line-height: ${reusedAt === 'masterSeries' ? '1.4rem' : '1.6rem'};
        height: ${reusedAt === 'masterSeries' ? 'auto;' : `${size === 'large' ? 3 : 2}rem;`};
        max-width: 95vw;
        border-radius: ${theme.radius.medium};
        padding: ${reusedAt === 'masterSeries' ? '0.8rem' : '0'} ${size === 'large' ? 1 : 0.9}rem;
      }
    font-weight: 400;
    :not(:disabled) {
      cursor: pointer;

      :hover {
        border-color: ${colorMap[color].hoverBorderColor};
        background-color: ${colorMap[color].hoverBackgroundColor};
        color: ${colorMap[color].hoverColor};
      }
    }

    :disabled {
      opacity: 0.1;
    }

    > svg {
      height: 2rem;
    }
  `,
);

interface LabelProps {
  hasIcon: boolean;
  hasRightIcon: boolean;
}

const Label = styled.span<LabelProps>`
  flex: 1;
  ${({ hasIcon }) => (hasIcon ? 'margin-left: 1rem;' : '')}
  ${({ hasRightIcon }) => (hasRightIcon ? 'margin-right: 1rem;' : '')}
`;

const StyledLink = StyledButton.withComponent(Link);

function Button({
  label,
  link,
  icon,
  rightIcon,
  children,
  reusedAt,
  ...rest
}: ButtonProps) {
  const Icon = icon && icons[icon as keyof typeof icons];
  const RightIcon = rightIcon && icons[rightIcon as keyof typeof icons];
  if (link) {
    return (
      <StyledLink to={link} hasLabel={Boolean(label)} reusedAt={reusedAt} {...rest} bold>
        {Icon && <Icon />}
        {label && (
          <Label hasIcon={Boolean(Icon)} hasRightIcon={Boolean(RightIcon)}>
            {label}
          </Label>
        )}
        {RightIcon && <RightIcon />}
      </StyledLink>
    );
  }
  return (
    <StyledButton hasLabel={Boolean(label)} {...rest}>
      {Icon && <Icon />}
      {label && (
        <Label hasIcon={Boolean(Icon)} hasRightIcon={Boolean(RightIcon)}>
          {label}
        </Label>
      )}
      {/* children props we can send anything to show inside the button */}
      {children != 'Select' && children}
      {RightIcon && <RightIcon />}
    </StyledButton>
  );
}

Button.defaultProps = {
  size: 'medium',
  color: 'green',
  outlined: false,
  disabled: false,
} as Partial<ButtonProps>;

export default Button;
